import {parseURLQuery} from 'app/utils/location/parseURLQuery';

import {isClient} from '../isClient';
import {parseHashUrl} from '../urlStateElements/parseHashUrl';

import {historyChangeAction, setParsedURLStateAction} from './store/actions';

export class HashHistory {
    static #instance: InstanceType<typeof HashHistory>;
    #initialized = false;

    get location(): HashLocation {
        let pathname = '';
        let search = '';
        if (isClient()) {
            search = window.location.search;
            pathname = window.location.hash;

            if (window.location.hash.startsWith('#')) {
                pathname = pathname.substring(1);
            }

            if (!pathname.startsWith('/')) {
                pathname = `/${pathname}`;
            }
        }

        return {
            search,
            pathname,
        };
    }

    static getInstance() {
        if (!HashHistory.#instance) {
            HashHistory.#instance = new HashHistory();
        }
        return HashHistory.#instance;
    }

    init() {
        if (this.#initialized || typeof window === 'undefined' || !HashHistory.#instance) {
            return;
        }

        this.#initialized = true;

        window.addEventListener('popstate', () => {
            this.onChange();
        });

        this.onChange();
    }

    onChange() {
        historyChangeAction(this.location.pathname, this.location.search);
        const parsedHashState = parseHashUrl(this.location.pathname);
        const parsedQueryState = parseURLQuery(this.location.search);
        setParsedURLStateAction(parsedHashState, parsedQueryState);
    }

    /**
     * Update hash with notifying state.
     * @param path
     */
    push(path: string) {
        window.history.pushState(null, '', this.getHashPath(path));
    }

    /**
     * Update hash and notify state.
     * @param path
     */
    pushAndDispatch(path: string) {
        this.push(path);
        this.onChange();
    }

    /**
     * Update query string and notify state.
     * @param url
     */
    pushSearchAndDispatch(url: URL) {
        window.history.pushState(null, '', url);
        this.onChange();
    }

    replace(path: string) {
        window.history.replaceState(null, '', this.getHashPath(path));
        this.onChange();
    }

    removeHash() {
        window.history.replaceState(null, '', this.getCurrentUrl());
        this.onChange();
    }

    /**
     * Remove hash from url without notifying url listeners
     */
    removeHashSilently() {
        window.history.pushState(null, '', this.getCurrentUrl());
    }

    private getCurrentUrl() {
        return `${window.location.pathname}${window.location.search}`;
    }

    private getHashPath(path: string): string {
        return !path.startsWith('#') ? `#${path}` : path;
    }
}

type HashLocation = {
    pathname: string;
    search: string;
};
